import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export const ADMIN_PERMISSIONS = [
  { key: "manage_campings", label: "Hantera campingar", desc: "Godkänn, avvisa och redigera campingplatser", icon: "🏕️" },
  { key: "manage_tips", label: "Hantera resetips", desc: "Ta bort olämpliga resetips", icon: "📝" },
  { key: "manage_members", label: "Hantera medlemmar", desc: "Blockera/avblockera och ge moderator-roller", icon: "👥" },
  { key: "view_analytics", label: "Visa statistik", desc: "Se plattformsanalys och live-besökare", icon: "📊" },
  { key: "manage_reports", label: "Hantera rapporter", desc: "Granska och hantera felrapporter", icon: "⚠️" },
] as const;

export type AdminPermission = typeof ADMIN_PERMISSIONS[number]["key"];

export const useRoles = () => {
  const { user } = useAuth();

  const { data: roles = { isAdmin: false, isOwner: false, permissions: [] as string[] } } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      const [roleRes, permRes] = await Promise.all([
        supabase.rpc("has_role", { _user_id: user!.id, _role: "admin" }),
        supabase.from("admin_permissions" as any).select("permission").eq("user_id", user!.id),
      ]);

      const isAdmin = roleRes.data as boolean;

      // Check owner via rpc
      const ownerRes = await supabase.rpc("is_owner" as any, { _user_id: user!.id });
      const isOwner = ownerRes.data as boolean;

      const permissions = ((permRes.data as any[]) || []).map((p: any) => p.permission);

      return { isAdmin: isAdmin || isOwner, isOwner, permissions };
    },
    enabled: !!user,
  });

  const hasPermission = (perm: AdminPermission): boolean => {
    return roles.isOwner || roles.permissions.includes(perm);
  };

  return {
    isAdmin: roles.isAdmin,
    isOwner: roles.isOwner,
    permissions: roles.permissions,
    hasPermission,
  };
};
